/**
 * UserContext - Global state management for user authentication and profile data
 * Provides centralized user management with retry logic and error handling
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from "../lib/supabase";

const UserContext = createContext();

/**
 * Custom hook to access user context
 * Must be used within a UserProvider component
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  // User state management
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Configuration constants for network resilience
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000; // 2 seconds between retry attempts
  const LOADING_TIMEOUT = 15000; // 15 seconds maximum loading time

  /**
   * Fetch current user profile data with retry logic
   * @param {number} attempt - Current attempt number (1-based)
   * @returns {Object|null} User profile data or null on failure
   */
  const querySelf = async (attempt = 1) => {
    try {
      // Create a timeout promise to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000);
      });
      
      // Race between the actual request and timeout
      const queryPromise = supabase.rpc("query_self", {});
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
      
      if (error) {
        throw error;
      }
      
      const userData = data?.data?.[0];
      
      setUserProfile(userData);
      // Check admin status: admin_role >= 0 indicates admin privileges
      const adminStatus = userData?.admin_role !== null && userData?.admin_role !== undefined && userData?.admin_role >= 0;
      setIsAdmin(adminStatus);
      setRetryCount(0); // Reset retry count on success
      return userData;
    } catch (error) {
      // Implement exponential backoff retry strategy
      if (attempt < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return querySelf(attempt + 1);
      }
      
      // Max retries exceeded, set safe default state
      setUserProfile(null);
      setIsAdmin(false);
      setRetryCount(attempt);
      return null;
    }
  };

  /**
   * Check authentication status and load user profile
   * @param {number} attempt - Current attempt number (1-based)
   */
  const checkUserStatus = async (attempt = 1) => {
    try {
      setLoading(true);
      
      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Session timeout')), 8000);
      });
      
      // Race between session check and timeout to prevent hanging
      const sessionPromise = supabase.auth.getSession();
      const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise]);
      
      if (error) {
        throw error;
      }
      
      if (!session) {
        // No active session - user is not logged in
        setUserProfile(null);
        setIsAdmin(false);
        setRetryCount(0);
        setLoading(false);
        return;
      }

      // Session exists - fetch user profile
      await querySelf();
      setLoading(false);
    } catch (error) {
      // Implement retry with exponential backoff
      if (attempt < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return checkUserStatus(attempt + 1);
      }
      
      // Max retries exceeded, set safe state and stop loading
      setUserProfile(null);
      setIsAdmin(false);
      setRetryCount(attempt);
      setLoading(false);
    }
  };

  // Safety timeout to prevent infinite loading states
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        setLoading(false);
      }
    }, LOADING_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [loading]);

  useEffect(() => {
    checkUserStatus();

    // Listen for authentication state changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          setLoading(true);
          
          if (event === 'SIGNED_IN' && session) {
            // User logged in - fetch their profile
            await querySelf();
          } else if (event === 'SIGNED_OUT') {
            // User logged out - clear all user data
            setUserProfile(null);
            setIsAdmin(false);
            setRetryCount(0);
          }
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  /**
   * Manual refresh function for UI components
   * Resets retry count and re-checks user status
   */
  const refreshUserData = async () => {
    setRetryCount(0);
    await checkUserStatus();
  };

  /**
   * Logout function with cleanup
   * @returns {Object} Success/failure status with error details
   */
  const logout = async () => {
    try {
      setLoading(true);
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }

      // Clear any cached authentication data
      localStorage.removeItem("auth");
      
      // Reset all user-related state to initial values
      setUserProfile(null);
      setIsAdmin(false);
      setRetryCount(0);
      
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  // Context value object containing all user-related state and functions
  const value = {
    userProfile,     // Current user's profile data
    isAdmin,         // Whether user has admin privileges
    loading,         // Loading state for async operations
    retryCount,      // Number of failed attempts for debugging
    querySelf,       // Function to fetch user profile
    checkUserStatus, // Function to check authentication status
    refreshUserData, // Function to manually refresh user data
    logout           // Function to logout user
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 